/** @jsxImportSource preact */
import { useState, useEffect } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'

type FormAction =
  | 'move-to-defaulted'
  | 'move-to-defaulted-cash-refunds'
  | 'move-to-defaulted-final-email-sent-awaiting-asc'
  | 'move-to-collections-promise-to-pay-received'
  | 'mark-loss-recovered'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

export function LossRecovery() {
  const [bookingId, setBookingId] = useState('')
  const [selectedAction, setSelectedAction] = useState<FormAction>('move-to-defaulted')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  // Effect to extract booking ID from Front context
  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  // Form fields for move-to-defaulted
  const [xlLayByTerminationFee, setXlLayByTerminationFee] = useState('')
  const [xlCancellationDate, setXlCancellationDate] = useState('')
  const [xlCustomerTotalPaidToDate, setXlCustomerTotalPaidToDate] = useState('')
  const [dfFinalEmailSent, setDfFinalEmailSent] = useState('no')
  const [defaultNote, setDefaultNote] = useState('')

  // Set default note prefill
  useEffect(() => {
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `updated by ${username} in Front`
      setDefaultNote(notePrefill)
      setCashRefundsNote(notePrefill)
      setFinalEmailAwaitingNote(notePrefill)
      setLossRecoveredNote(notePrefill)
      setPromiseToPayNote(notePrefill)
    }
  }, [user])

  // Form fields for move-to-defaulted-cash-refunds
  const [rfAmountPromisedToCustomer, setRfAmountPromisedToCustomer] = useState('')
  const [rfReleaseToCustomerDate, setRfReleaseToCustomerDate] = useState('')
  const [rfSupplierTotalCashRefund, setRfSupplierTotalCashRefund] = useState('')
  const [rfSupplierExpectRefundBackDate, setRfSupplierExpectRefundBackDate] = useState('')
  const [rfCustomerAdvisedRefundWouldBeSentDate, setRfCustomerAdvisedRefundWouldBeSentDate] = useState('')
  const [cashRefundsNote, setCashRefundsNote] = useState('')

  // Form fields for move-to-defaulted-final-email-sent-awaiting-asc
  const [
    xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided,
    setXlStripeSubscriptionCancelledAndUnpaidInvoicesVoided,
  ] = useState(false)
  const [xlCancelledWithSupplierRequestedRefund, setXlCancelledWithSupplierRequestedRefund] = useState('yes')
  const [finalEmailAwaitingNote, setFinalEmailAwaitingNote] = useState('')

  // Form fields for move-to-collections-promise-to-pay-received
  const [promiseToPayDate, setPromiseToPayDate] = useState('')
  const [promiseToPayNote, setPromiseToPayNote] = useState('')

  // Loss Recovered fields
  const [lossRecoveredNote, setLossRecoveredNote] = useState('')

  const resetForm = () => {
    setFormStatus('idle')
    setErrorMessage('')
    // Only reset form fields, not the booking ID

    // move-to-defaulted fields
    setXlLayByTerminationFee('')
    setXlCancellationDate('')
    setXlCustomerTotalPaidToDate('')
    setDfFinalEmailSent('no')

    // move-to-defaulted-cash-refunds fields
    setRfAmountPromisedToCustomer('')
    setRfReleaseToCustomerDate('')
    setRfSupplierTotalCashRefund('')
    setRfSupplierExpectRefundBackDate('')
    setRfCustomerAdvisedRefundWouldBeSentDate('')

    // move-to-defaulted-final-email-sent-awaiting-asc fields
    setXlStripeSubscriptionCancelledAndUnpaidInvoicesVoided(false)
    setXlCancelledWithSupplierRequestedRefund('yes')

    // move-to-collections-promise-to-pay-received fields
    setPromiseToPayDate('')

    // mark-loss-recovered fields
    setLossRecoveredNote('')

    // Reset note fields with the username prefill
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `updated by ${username} in Front`
      setDefaultNote(notePrefill)
      setCashRefundsNote(notePrefill)
      setFinalEmailAwaitingNote(notePrefill)
      setLossRecoveredNote(notePrefill)
      setPromiseToPayNote(notePrefill)
    } else {
      setDefaultNote('')
      setCashRefundsNote('')
      setFinalEmailAwaitingNote('')
      setPromiseToPayNote('')
    }
  }

  const handleActionChange = (e: Event) => {
    const target = e.target as HTMLSelectElement
    setSelectedAction(target.value as FormAction)
    resetForm()
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault()

    if (!bookingId) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    setFormStatus('loading')

    try {
      let endpoint = ''
      let payload = {}

      switch (selectedAction) {
        case 'move-to-defaulted':
          endpoint = `/bookings/${bookingId.trim()}/move-to-defaulted`
          payload = {
            'xl-lay-by-termination-fee': parseFloat(xlLayByTerminationFee),
            'xl-cancellation-date': xlCancellationDate,
            'xl-customer-total-paid-to-date': parseFloat(xlCustomerTotalPaidToDate),
            'df-final-email-sent': dfFinalEmailSent,
            note: defaultNote,
          }
          break
        case 'move-to-defaulted-cash-refunds':
          endpoint = `/bookings/${bookingId.trim()}/move-to-defaulted-cash-refunds`
          payload = {
            'rf-amount-promised-to-customer': parseFloat(rfAmountPromisedToCustomer),
            'rf-release-to-customer-date': rfReleaseToCustomerDate,
            'rf-supplier-total-cash-refund': parseFloat(rfSupplierTotalCashRefund),
            'rf-supplier-expect-refund-back-date': rfSupplierExpectRefundBackDate,
            'rf-customer-advised-refund-would-be-sent-date': rfCustomerAdvisedRefundWouldBeSentDate,
            note: cashRefundsNote,
          }
          break
        case 'move-to-defaulted-final-email-sent-awaiting-asc':
          endpoint = `/bookings/${bookingId.trim()}/move-to-defaulted-final-email-sent-awaiting-asc`
          payload = {
            'xl-stripe-subscription-cancelled-and-unpaid-invoices-voided':
              xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided,
            'xl-cancelled-with-supplier-requested-refund': xlCancelledWithSupplierRequestedRefund,
            note: finalEmailAwaitingNote,
          }
          break
        case 'move-to-collections-promise-to-pay-received':
          endpoint = `/bookings/${bookingId.trim()}/move-to-collections-promise-to-pay-received`
          payload = {
            'promise-to-pay-date': promiseToPayDate,
            note: promiseToPayNote,
          }
          break
        case 'mark-loss-recovered':
          endpoint = `/bookings/${bookingId.trim()}/mark-loss-recovered`
          payload = {
            note: lossRecoveredNote,
          }
          break
      }

      const response = await fetch(`${config.apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include', // Include cookies in the request
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'An error occurred')
      }

      setFormStatus('success')
      // Clear form fields on success
      resetForm()
      setFormStatus('success')
    } catch (error) {
      setFormStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Loss Recovery</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Loss Recovery</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <input
          id="bookingId"
          type="text"
          value={bookingId}
          onChange={e => setBookingId((e.target as HTMLInputElement).value)}
          className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
          placeholder="Enter Booking ID"
          required
        />
        {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
        {bookingId && isFrontApp && (
          <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
        )}
      </div>

      <div className="mb-6">
        <label
          htmlFor="action"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Select Action
        </label>
        <select
          id="action"
          value={selectedAction}
          onChange={handleActionChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="move-to-defaulted">Move to Defaulted</option>
          <option value="move-to-defaulted-cash-refunds">Move to Defaulted Cash Refunds</option>
          <option value="move-to-defaulted-final-email-sent-awaiting-asc">
            Move to Defaulted Final Email Sent Awaiting ASC
          </option>
          <option value="move-to-collections-promise-to-pay-received">
            Move to Collections Promise to Pay Received
          </option>
          <option value="mark-loss-recovered">Mark Loss Recovered</option>
        </select>
      </div>

      <form onSubmit={handleSubmit}>
        {selectedAction === 'move-to-defaulted' && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="xlLayByTerminationFee"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                XL Lay By Termination Fee
              </label>
              <input
                id="xlLayByTerminationFee"
                type="number"
                step="0.01"
                value={xlLayByTerminationFee}
                onChange={e => setXlLayByTerminationFee((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label
                htmlFor="xlCancellationDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                XL Cancellation Date
              </label>
              <input
                id="xlCancellationDate"
                type="date"
                value={xlCancellationDate}
                onChange={e => setXlCancellationDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="xlCustomerTotalPaidToDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                XL Customer Total Paid To Date
              </label>
              <input
                id="xlCustomerTotalPaidToDate"
                type="number"
                step="0.01"
                value={xlCustomerTotalPaidToDate}
                onChange={e => setXlCustomerTotalPaidToDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label
                htmlFor="dfFinalEmailSent"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                DF Final Email Sent
              </label>
              <select
                id="dfFinalEmailSent"
                value={dfFinalEmailSent}
                onChange={e => setDfFinalEmailSent((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="defaultNote"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note
              </label>
              <textarea
                id="defaultNote"
                value={defaultNote}
                onChange={e => setDefaultNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                placeholder="Enter additional notes"
                required
              />
            </div>
          </div>
        )}

        {selectedAction === 'mark-loss-recovered' && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="lossRecoveredNote"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note
              </label>
              <textarea
                id="lossRecoveredNote"
                value={lossRecoveredNote}
                onChange={e => setLossRecoveredNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                required
              ></textarea>
            </div>
          </div>
        )}

        {selectedAction === 'move-to-defaulted-cash-refunds' && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="rfAmountPromisedToCustomer"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                RF Amount Promised To Customer
              </label>
              <input
                id="rfAmountPromisedToCustomer"
                type="number"
                step="0.01"
                value={rfAmountPromisedToCustomer}
                onChange={e => setRfAmountPromisedToCustomer((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label
                htmlFor="rfReleaseToCustomerDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                RF Release To Customer Date
              </label>
              <input
                id="rfReleaseToCustomerDate"
                type="date"
                value={rfReleaseToCustomerDate}
                onChange={e => setRfReleaseToCustomerDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="rfSupplierTotalCashRefund"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                RF Supplier Total Cash Refund
              </label>
              <input
                id="rfSupplierTotalCashRefund"
                type="number"
                step="0.01"
                value={rfSupplierTotalCashRefund}
                onChange={e => setRfSupplierTotalCashRefund((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label
                htmlFor="rfSupplierExpectRefundBackDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                RF Supplier Expect Refund Back Date
              </label>
              <input
                id="rfSupplierExpectRefundBackDate"
                type="date"
                value={rfSupplierExpectRefundBackDate}
                onChange={e => setRfSupplierExpectRefundBackDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="rfCustomerAdvisedRefundWouldBeSentDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                RF Customer Advised Refund Would Be Sent Date
              </label>
              <input
                id="rfCustomerAdvisedRefundWouldBeSentDate"
                type="date"
                value={rfCustomerAdvisedRefundWouldBeSentDate}
                onChange={e => setRfCustomerAdvisedRefundWouldBeSentDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="cashRefundsNote"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note
              </label>
              <textarea
                id="cashRefundsNote"
                value={cashRefundsNote}
                onChange={e => setCashRefundsNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                placeholder="Enter additional notes"
                required
              />
            </div>
          </div>
        )}

        {selectedAction === 'move-to-defaulted-final-email-sent-awaiting-asc' && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="xlStripeSubscriptionCancelled"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                XL Stripe Subscription Cancelled And Unpaid Invoices Voided
              </label>
              <div className="mt-1">
                <label className="inline-flex items-center mr-6">
                  <input
                    type="checkbox"
                    checked={xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided}
                    onChange={e =>
                      setXlStripeSubscriptionCancelledAndUnpaidInvoicesVoided((e.target as HTMLInputElement).checked)
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Yes</span>
                </label>
              </div>
            </div>
            <div>
              <label
                htmlFor="xlCancelledWithSupplierRequestedRefund"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                XL Cancelled With Supplier Requested Refund
              </label>
              <select
                id="xlCancelledWithSupplierRequestedRefund"
                value={xlCancelledWithSupplierRequestedRefund}
                onChange={e => setXlCancelledWithSupplierRequestedRefund((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="yes">Yes</option>
                <option value="n/a">N/A</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="finalEmailAwaitingNote"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note
              </label>
              <textarea
                id="finalEmailAwaitingNote"
                value={finalEmailAwaitingNote}
                onChange={e => setFinalEmailAwaitingNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                placeholder="Enter additional notes"
                required
              />
            </div>
          </div>
        )}

        {selectedAction === 'move-to-collections-promise-to-pay-received' && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="promiseToPayDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Promise to Pay Date
              </label>
              <input
                id="promiseToPayDate"
                type="date"
                value={promiseToPayDate}
                onChange={e => setPromiseToPayDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="promiseToPayNote"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note
              </label>
              <textarea
                id="promiseToPayNote"
                value={promiseToPayNote}
                onChange={e => setPromiseToPayNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                placeholder="Enter additional notes"
                required
              />
            </div>
          </div>
        )}

        {formStatus === 'success' && (
          <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600 font-medium">Operation completed successfully for booking {bookingId}!</p>
          </div>
        )}

        {formStatus === 'error' && (
          <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 font-medium">Error: {errorMessage}</p>
          </div>
        )}

        <div className="mt-6">
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={resetForm}
              className="w-1/3 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={formStatus === 'loading'}
              className="w-2/3 px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
